import { useState, useEffect } from 'react';
import IconSvg from 'src/assets/icons/IconSvg';
import { useViewport } from 'src/context/ViewportContext';
import { useLeaderboard } from 'src/hooks/useLeaderboard';
import SortableColumnHeader from 'src/components/leaderboard/SortableColumnHeader';
import { satsToBtc } from 'src/utils/raffleHelpers';
import type { CollectionEntry, SortState, LeaderboardPeriod } from 'src/types/leaderboard';

interface Props {
  search: string;
  period: LeaderboardPeriod;
}

const LeaderboardCollectionTab = ({ search, period }: Props) => {
  const { isMobile } = useViewport();
  const { entries, meta, isLoading, fetchLeaderboard } =
    useLeaderboard<CollectionEntry>('collections');

  const [sort, setSort] = useState<SortState>({ field: 'volume', order: 'desc' });
  const [page, setPage] = useState(1);

  useEffect(() => {
    fetchLeaderboard({
      search: search || undefined,
      sortBy: sort.field || undefined,
      sortOrder: sort.field ? sort.order : undefined,
      page,
      limit: 20,
      period,
    });
  }, [search, sort, page, period, fetchLeaderboard]);

  const handleSort = (newSort: SortState) => {
    setSort(newSort);
    setPage(1);
  };

  const renderRow = (entry: CollectionEntry) => {
    const btcValue = satsToBtc(entry.volumeSats);
    const usdEstimate = (btcValue * 100000).toFixed(2);

    return (
      <div
        key={entry.collectionName}
        className="grid grid-cols-[0.5fr_1fr_1fr_1fr_0.6fr] items-center px-5 py-4 text-lg rounded-lg border border-transparent transition-all duration-200 cursor-pointer text-grayscale-300 hover:bg-grayscale-700"
      >
        <div className="w-[52px]">{entry.rank}</div>

        <div className="flex gap-3 items-center">
          {entry.imageUrl ? (
            <img
              src={entry.imageUrl}
              alt={entry.collectionName}
              className="flex-shrink-0 w-12 h-12 rounded-full object-cover"
            />
          ) : (
            <div className="flex-shrink-0 w-12 h-12 bg-grayscale-700 rounded-full" />
          )}
          <div className="text-brand-magnolia font-semibold truncate">
            {entry.collectionName}
          </div>
        </div>

        <div>{entry.rafflesCreated.toLocaleString()}</div>

        <div>{Number(entry.ticketsSold).toLocaleString()}</div>

        <div>
          <div className="flex gap-1 items-center text-grayscale-100">
            <IconSvg icon="bitcoinS" />
            <div className="text-lg font-semibold text-grayscale-100">
              {btcValue.toFixed(4)}
            </div>
          </div>
          <div className="text-xs text-grayscale-400">
            &asymp; ${Number(usdEstimate).toLocaleString()}
          </div>
        </div>
      </div>
    );
  };

  const renderMobileCard = (entry: CollectionEntry) => {
    const btcValue = satsToBtc(entry.volumeSats);
    const usdEstimate = (btcValue * 100000).toFixed(2);

    return (
      <div key={entry.collectionName} className="p-4 rounded-lg border border-transparent bg-grayscale-800">
        <div className="flex gap-3 items-center">
          {entry.imageUrl ? (
            <img
              src={entry.imageUrl}
              alt={entry.collectionName}
              className="flex-shrink-0 w-12 h-12 rounded-full object-cover"
            />
          ) : (
            <div className="flex-shrink-0 w-12 h-12 bg-grayscale-700 rounded-full" />
          )}
          <div>
            <div className="text-grayscale-400">#{entry.rank}</div>
            <div className="text-brand-magnolia font-semibold">{entry.collectionName}</div>
          </div>
        </div>

        <div className="my-3 border-t border-grayscale-700" />

        <div className="flex justify-between">
          <div className="space-y-1">
            <div className="text-xs text-brand-granite">Raffles Created</div>
            <div className="text-lg font-semibold text-grayscale-100">
              {entry.rafflesCreated.toLocaleString()}
            </div>
          </div>

          <div className="space-y-1">
            <div className="text-xs text-brand-granite">Tickets Sold</div>
            <div className="text-lg font-semibold text-grayscale-100">
              {Number(entry.ticketsSold).toLocaleString()}
            </div>
          </div>

          <div className="space-y-1 text-right">
            <div className="text-xs text-brand-granite">Volume</div>
            <div className="flex gap-1 justify-end items-center text-grayscale-100">
              <IconSvg icon="bitcoinS" />
              <div className="text-lg font-semibold text-grayscale-100">
                {btcValue.toFixed(4)}
              </div>
            </div>
            <div className="text-xs text-grayscale-400">
              &asymp; ${Number(usdEstimate).toLocaleString()}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading && entries.length === 0) {
    return <div className="text-grayscale-400 text-center py-10">Loading...</div>;
  }

  return (
    <>
      {!isMobile ? (
        <div className="w-full rounded-lg bg-grayscale-800">
          <div className="grid grid-cols-[0.5fr_1fr_1fr_1fr_0.6fr] py-2.5 px-5 text-sm text-left text-grayscale-300">
            <div>Rank</div>
            <div>Name</div>
            <SortableColumnHeader label="Raffles Created" field="rafflesCreated" currentSort={sort} onSort={handleSort} />
            <SortableColumnHeader label="Tickets Sold" field="ticketsSold" currentSort={sort} onSort={handleSort} />
            <SortableColumnHeader label="Volume" field="volume" currentSort={sort} onSort={handleSort} />
          </div>

          <div>
            {entries.map((entry) => renderRow(entry))}
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {entries.map((entry) => renderMobileCard(entry))}
        </div>
      )}

      {meta && meta.totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          <button
            className="px-3 py-1.5 rounded-lg bg-grayscale-800 text-grayscale-300 disabled:opacity-50"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            Prev
          </button>
          <span className="px-3 py-1.5 text-grayscale-400">
            {page} / {meta.totalPages}
          </span>
          <button
            className="px-3 py-1.5 rounded-lg bg-grayscale-800 text-grayscale-300 disabled:opacity-50"
            disabled={page >= meta.totalPages}
            onClick={() => setPage(page + 1)}
          >
            Next
          </button>
        </div>
      )}
    </>
  );
};

export default LeaderboardCollectionTab;
